import React from 'react';
import { Link } from 'react-router-dom';
import {
    Workflow,
    Bot,
    Users,
    Megaphone,
    Share2,
    Cloud,
    ArrowRight,
    CheckCircle2,
    Zap,
    Layers,
    Clock,
    ShieldCheck,
    TrendingUp
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernAiAutomation = () => {
    const services = [
        {
            icon: <Workflow size={28} />,
            title: 'Internal Automations',
            description: 'Take repetitive work off your team by automating the processes behind your operations.',
            features: ['Back-office tasks', 'Approvals', 'Reporting', 'Data entry']
        },
        {
            icon: <Users size={28} />,
            title: 'Customer-Facing Automations',
            description: 'Respond faster and stay consistent at every point where customers reach you.',
            features: ['Customer support', 'Lead handling', 'Onboarding', 'Follow-ups']
        },
        {
            icon: <Bot size={28} />,
            title: 'In-App AI Copilots',
            description: 'Add AI assistants inside your applications that help users get work done where they already are.',
            features: ['Embedded assistants', 'Answers from your data', 'Guided tasks', 'App integration']
        },
        {
            icon: <Megaphone size={28} />,
            title: 'AI Social Media Content Workflows',
            description: 'Run your content pipeline from idea to published post, with people reviewing before anything goes live.',
            features: ['Content generation', 'Human review', 'Scheduling', 'Publishing']
        },
        {
            icon: <Share2 size={28} />,
            title: 'Make & n8n Workflow Builds',
            description: 'Design and build workflows in Make and n8n that connect the tools you already use.',
            features: ['Workflow design', 'Custom builds', 'App integrations', 'Documentation']
        },
        {
            icon: <Cloud size={28} />,
            title: 'AWS Deployment & Support',
            description: 'Deploy your automations on AWS and keep them running reliably after launch.',
            features: ['AWS hosting', 'Monitoring', 'Maintenance', 'Ongoing improvements']
        }
    ];

    const outcomes = [
        {
            icon: <Clock size={28} />,
            title: 'Time Back for Your Team',
            description: 'Routine work runs on its own so people can focus on higher-value tasks'
        },
        {
            icon: <TrendingUp size={28} />,
            title: 'Consistent Output',
            description: 'Every request and every post follows the same reliable process'
        },
        {
            icon: <ShieldCheck size={28} />,
            title: 'Human Oversight',
            description: 'Review steps keep your team in control of what AI produces'
        }
    ];

    const features = [
        { icon: <Workflow size={24} />, title: 'Internal', text: 'Team automations' },
        { icon: <Users size={24} />, title: 'External', text: 'Customer automations' },
        { icon: <Bot size={24} />, title: 'Copilots', text: 'AI inside your apps' },
        { icon: <Cloud size={24} />, title: 'On AWS', text: 'Deployed and supported' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="AI Automation & Workflows"
                subtitle="Automations, AI Copilots, and Content Workflows Built for Your Business"
                breadcrumbs={[
                    { label: 'Solutions', path: '/solutions' },
                    { label: 'AI Automation & Workflows' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Zap size={16} />
                                AI Automation
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Spending Too Much Time on
                                <span className="highlight"> Repetitive Tasks?</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We help companies hand that work to AI. We create internal and external
                                automations, build AI copilots into your apps, and set up AI social media
                                content workflows in Make and n8n, then deploy them on AWS so they keep
                                running.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Discuss Your Automation
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    Explore All Services
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Layers size={16} />
                            What We Build
                        </span>
                        <h2 className="jk-solutions-services__title">
                            AI Automation
                            <span className="highlight"> Services</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            From a single workflow to AI copilots across your apps
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-solutions-services__card-title">{service.title}</h3>
                                <p className="jk-solutions-services__card-description">{service.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcomes Section */}
            <section className="jk-solutions-outcomes">
                <div className="jk-solutions-outcomes__container">
                    <div className="jk-solutions-outcomes__header">
                        <h2 className="jk-solutions-outcomes__title">
                            What You Get
                        </h2>
                    </div>

                    <div className="jk-solutions-outcomes__grid">
                        {outcomes.map((outcome, index) => (
                            <div key={index} className="jk-solutions-outcomes__card">
                                <div className="jk-solutions-outcomes__card-icon">
                                    {outcome.icon}
                                </div>
                                <h3 className="jk-solutions-outcomes__card-title">{outcome.title}</h3>
                                <p className="jk-solutions-outcomes__card-text">{outcome.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernAiAutomation;
